import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import Logo from './Logo';
import { useAuth } from '../context/AuthContext';
import { LayoutDashboard, Users, FileText, Package, LogOut, PlusCircle, Bell, Search, UserCog, KeyRound, Loader2, KanbanSquare, Menu, X } from 'lucide-react';
import { Avatar, AvatarFallback } from './ui/avatar';
import { Input } from './ui/input';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { Label } from './ui/label';
import { authApi } from '../lib/api';
import { toast } from 'sonner';

const NAV = [
  { to: '/app', label: 'Dashboard', icon: LayoutDashboard, exact: true },
  { to: '/app/leads', label: 'Leads', icon: Users },
  { to: '/app/pipeline', label: 'Pipeline', icon: KanbanSquare },
  { to: '/app/proposals', label: 'Proposals', icon: FileText },
  { to: '/app/bom', label: 'BOM Library', icon: Package },
];

const ADMIN_NAV = [
  { to: '/app/team', label: 'Team', icon: UserCog, adminOnly: true },
];

export default function AppShell({ children }) {
  const loc = useLocation();
  const nav = useNavigate();
  const { user, logout } = useAuth();
  const active = (item) => item.exact ? loc.pathname === item.to : loc.pathname.startsWith(item.to);
  const [pwOpen, setPwOpen] = useState(false);
  const [pwForm, setPwForm] = useState({ current_password: '', new_password: '', confirm_password: '' });
  const [pwSaving, setPwSaving] = useState(false);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);

  // Close mobile menu on route change
  React.useEffect(() => { setMobileNavOpen(false); }, [loc.pathname]);

  const submitPw = async () => {
    if (pwForm.new_password.length < 6) { toast.error('New password must be at least 6 characters'); return; }
    if (pwForm.new_password !== pwForm.confirm_password) { toast.error('New password and confirmation do not match'); return; }
    setPwSaving(true);
    try {
      await authApi.changePassword(pwForm.current_password, pwForm.new_password);
      toast.success('Password updated successfully');
      setPwOpen(false);
      setPwForm({ current_password: '', new_password: '', confirm_password: '' });
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not update password');
    } finally { setPwSaving(false); }
  };

  return (
    <div className="min-h-screen bg-emerald-50/30 flex">
      {/* Mobile backdrop */}
      {mobileNavOpen && (
        <div className="lg:hidden fixed inset-0 bg-black/40 z-40" onClick={() => setMobileNavOpen(false)} />
      )}
      <aside className={`fixed lg:sticky lg:top-0 lg:h-screen top-0 left-0 z-50 w-64 bg-white border-r border-emerald-100 flex flex-col h-screen transition-transform lg:transform-none ${mobileNavOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}`}>
        <div className="px-5 py-5 border-b border-emerald-100 flex items-center justify-between">
          <Link to="/"><Logo /></Link>
          <button className="lg:hidden p-1 text-slate-500" onClick={() => setMobileNavOpen(false)} aria-label="close menu"><X className="w-5 h-5" /></button>
        </div>
        <nav className="flex-1 px-3 py-4 space-y-1">
          {NAV.map(item => {
            const Icon = item.icon;
            const isActive = active(item);
            return (
              <Link key={item.to} to={item.to}
                className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${isActive ? 'bg-emerald-700 text-white shadow-sm shadow-emerald-200' : 'text-slate-700 hover:bg-emerald-50 hover:text-emerald-800'}`}>
                <Icon className="w-4 h-4" />
                {item.label}
              </Link>
            );
          })}
          {user && (user.role === 'Director' || user.role === 'Admin') && (
            <>
              <div className="px-3 pt-4 pb-1 text-[10px] font-bold tracking-widest text-slate-400 uppercase">Admin</div>
              {ADMIN_NAV.map(item => {
                const Icon = item.icon;
                const isActive = active(item);
                return (
                  <Link key={item.to} to={item.to}
                    className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${isActive ? 'bg-emerald-700 text-white shadow-sm shadow-emerald-200' : 'text-slate-700 hover:bg-emerald-50 hover:text-emerald-800'}`}>
                    <Icon className="w-4 h-4" />
                    {item.label}
                  </Link>
                );
              })}
            </>
          )}
        </nav>
        <div className="px-3 pb-4">
          <Button onClick={() => nav('/app/proposals/new')} className="w-full bg-amber-500 hover:bg-amber-600 text-emerald-950 font-semibold">
            <PlusCircle className="w-4 h-4 mr-2" /> New Proposal
          </Button>
        </div>
        <div className="px-3 pb-4 border-t border-emerald-100 pt-4">
          <div className="flex items-center gap-3 px-2">
            <Avatar className="w-9 h-9"><AvatarFallback className="bg-emerald-700 text-white text-sm">{user?.avatar || 'SK'}</AvatarFallback></Avatar>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-semibold text-slate-800 truncate">{user?.name}</div>
              <div className="text-xs text-slate-500 truncate">{user?.role}</div>
            </div>
            <button onClick={() => setPwOpen(true)} className="text-slate-400 hover:text-orange-600" title="Change Password" data-testid="change-password-btn"><KeyRound className="w-4 h-4" /></button>
            <button onClick={() => { logout(); nav('/'); }} className="text-slate-400 hover:text-rose-600" title="Logout"><LogOut className="w-4 h-4" /></button>
          </div>
        </div>
      </aside>

      {/* Change Password dialog */}
      <Dialog open={pwOpen} onOpenChange={setPwOpen}>
        <DialogContent className="sm:max-w-[440px]">
          <DialogHeader>
            <DialogTitle className="text-emerald-950 flex items-center gap-2"><KeyRound className="w-5 h-5 text-orange-600" /> Change Password</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label className="mb-1.5 block">Current Password</Label>
              <Input type="password" value={pwForm.current_password} onChange={e => setPwForm({...pwForm, current_password: e.target.value})} placeholder="Your temporary or current password" data-testid="current-password" />
            </div>
            <div>
              <Label className="mb-1.5 block">New Password</Label>
              <Input type="password" value={pwForm.new_password} onChange={e => setPwForm({...pwForm, new_password: e.target.value})} placeholder="Min 6 characters" data-testid="new-password" />
            </div>
            <div>
              <Label className="mb-1.5 block">Confirm New Password</Label>
              <Input type="password" value={pwForm.confirm_password} onChange={e => setPwForm({...pwForm, confirm_password: e.target.value})} placeholder="Re-enter new password" data-testid="confirm-password" />
            </div>
            <div className="text-xs text-slate-500">Keep it safe. You&apos;ll need to log in again with the new password on other devices.</div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPwOpen(false)} disabled={pwSaving}>Cancel</Button>
            <Button className="bg-orange-600 hover:bg-orange-700 text-white" onClick={submitPw} disabled={pwSaving} data-testid="submit-change-password">
              {pwSaving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />} Update Password
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <main className="flex-1 min-w-0">
        <div className="h-16 bg-white border-b border-emerald-100 px-4 lg:px-6 flex items-center justify-between sticky top-0 z-30">
          <div className="flex items-center gap-2 flex-1 max-w-md">
            <button className="lg:hidden p-2 -ml-2 rounded-lg hover:bg-emerald-50 text-slate-700" onClick={() => setMobileNavOpen(true)} aria-label="open menu" data-testid="mobile-menu-btn">
              <Menu className="w-6 h-6" />
            </button>
            <Search className="w-4 h-4 text-slate-400 hidden sm:block" />
            <Input placeholder="Search…" className="border-0 focus-visible:ring-0 bg-transparent hidden sm:block" />
          </div>
          <div className="flex items-center gap-1.5 lg:gap-3">
            <Link to="/app/pipeline" className="lg:hidden inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-emerald-700 hover:bg-emerald-800 text-white text-xs font-bold shadow-md shadow-emerald-200" data-testid="mobile-crm-quick">
              <KanbanSquare className="w-3.5 h-3.5" /> CRM
            </Link>
            <button className="relative p-2 rounded-lg hover:bg-emerald-50">
              <Bell className="w-5 h-5 text-slate-600" />
              <span className="absolute top-1.5 right-1.5 w-2 h-2 bg-amber-500 rounded-full" />
            </button>
            <div className="hidden md:block text-right">
              <div className="text-xs text-slate-500">Welcome back</div>
              <div className="text-sm font-semibold text-emerald-900">{user?.name}</div>
            </div>
          </div>
        </div>
        <div className="p-4 lg:p-8">{children}</div>
      </main>
    </div>
  );
}
